import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Product, transformProduct } from "@/data/products";

export const useProducts = (category?: string) => {
  return useQuery({
    queryKey: ["products", category],
    queryFn: async (): Promise<Product[]> => {
      let query = supabase
        .from("products")
        .select("*")
        .eq("in_stock", true)
        .order("is_best_seller", { ascending: false })
        .order("created_at", { ascending: false });

      if (category && category !== "All") {
        query = query.contains("categories", [category]);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(transformProduct);
    },
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ["product", id],
    queryFn: async (): Promise<Product | null> => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        if (error.code === "PGRST116") return null; // Not found
        throw error;
      }

      return data ? transformProduct(data) : null;
    },
    enabled: !!id,
  });
};

export const useSimilarProducts = (product: Product | null) => {
  return useQuery({
    queryKey: ["similar-products", product?.id, product?.category],
    queryFn: async (): Promise<Product[]> => {
      if (!product) return [];

      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("category", product.category)
        .eq("in_stock", true)
        .neq("id", product.id)
        .limit(3);

      if (error) throw error;

      return (data || []).map(transformProduct);
    },
    enabled: !!product,
  });
};
